import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../providers/auth-provider";
import { useConfig } from "../providers/config-provider";
import { useNotification } from "../providers/notification-provider";
import SessionStateScreen from "../components/auth/SessionStateScreen";
import { loginRequest, fetchPublicInfo } from "../lib/api";
import { extractApiErrorMessage } from "../utils/apiErrorUtils";
import { consumeAuthRedirectContext } from "../utils/authRedirectContext";
import logo from "../assets/logo.png";
import { EyeIcon, EyeSlashIcon, UserIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

const schema = z.object({
  email: z.string().email("Informe um email válido"),
  password: z.string().min(1, "Informe a senha"),
  rememberMe: z.boolean().optional()
});

export default function LoginPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const config = useConfig();
  const { showToast } = useNotification();
  const [showPassword, setShowPassword] = useState(false);
  const [supportEmail, setSupportEmail] = useState(null);
  const [authNotice, setAuthNotice] = useState(null);

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "", rememberMe: true }
  });

  useEffect(() => { document.title = "Login | Orion WaiterCall"; }, []);

  useEffect(() => {
    const redirectContext = consumeAuthRedirectContext();
    if (!redirectContext) {
      return;
    }

    if (redirectContext.reason === "session_expired") {
      setAuthNotice({
        tone: "warning",
        title: "Sessão expirada",
        message:
          redirectContext.message ||
          "Sua sessão foi encerrada. Faça login novamente para continuar.",
      });
      return;
    }

    if (redirectContext.reason === "access_blocked") {
      setAuthNotice({
        tone: "error",
        title: "Acesso bloqueado",
        message:
          redirectContext.message ||
          "O acesso da sua empresa está temporariamente bloqueado.",
      });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchPublicInfo(config.apiBase)
      .then((res) => {
        if (!cancelled && res.data?.supportEmail) {
          setSupportEmail(res.data.supportEmail);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [config.apiBase]);

  const mutation = useMutation({
    mutationFn: async (values) => {
      const baseToUse = config.apiBase;
      const rememberMe = values.rememberMe !== false;
      const response = await loginRequest(baseToUse, values.email, values.password, rememberMe);
      return { payload: response.data, apiBase: baseToUse };
    },
    onSuccess: (data, values) => {
      try {
        auth.login(data.payload, { rememberMe: values.rememberMe !== false });
        navigate("/app", { replace: true });
      } catch (error) {
        const message = extractApiErrorMessage(
          error,
          "Não foi possível concluir o login com a resposta recebida do servidor.",
        );
        setError("root", { type: "manual", message });
        showToast(message, "error");
      }
    },
    onError: (error) => {
      console.error("Login error details:", error);
      const message = extractApiErrorMessage(error, "Não foi possível autenticar. Verifique as credenciais e tente novamente.");
      setError("root", { type: "manual", message });
      showToast(message, "error");
    }
  });

  if (auth.isLoading) {
    return (
      <SessionStateScreen
        variant="loading"
        title="Verificando sua sessão"
        message="Estamos conferindo o estado atual do login antes de decidir a navegação inicial."
      />
    );
  }

  if (auth.isAuthenticated && !auth.sessionIssue) return <Navigate to="/app" replace />;

  const onSubmit = (values) => {
    clearErrors("root");
    mutation.mutate(values);
  };

  return (
    <div className="flex min-h-screen flex-1">
      {/* Left Side */}
      <div className="relative hidden w-0 flex-1 lg:block">
        <div className="absolute inset-0 h-full w-full bg-gradient-to-br from-indigo-600 to-blue-800">
          <div className="flex h-full flex-col justify-center px-12 text-white">
            <h1 className="text-4xl font-bold tracking-tight">Orion WaiterCall</h1>
            <p className="mt-4 text-lg text-indigo-100">
              Gestão ágil para o seu restaurante. Simplifique o atendimento e encante.
            </p>
            <div className="mt-10 space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 backdrop-blur">
                  <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-indigo-50">Chamados via QR Code</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 backdrop-blur">
                   <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <span className="text-indigo-50">Atendimento em tempo real</span>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/20 to-transparent"></div>
        </div>
      </div>

      {/* Right Side */}
      <div className="relative flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24 bg-white">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div className="mb-8 flex flex-col items-center text-center">
            <img src={logo} alt="Orion WaiterCall" className="h-auto w-32 sm:w-40 lg:w-48" />
          </div>

          <div className="text-center">
            <h2 className="text-2xl font-bold leading-9 tracking-tight text-gray-900">Bem-vindo de volta</h2>
            <p className="mt-2 text-sm leading-6 text-gray-500">Insira suas credenciais para acessar.</p>
          </div>

          <div className="mt-10">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {authNotice && (
                <div
                  className={clsx(
                    "rounded-2xl border p-4 text-left",
                    authNotice.tone === "error"
                      ? "border-rose-200 bg-rose-50"
                      : "border-amber-200 bg-amber-50",
                  )}
                >
                  <p
                    className={clsx(
                      "text-sm font-semibold",
                      authNotice.tone === "error" ? "text-rose-900" : "text-amber-900",
                    )}
                  >
                    {authNotice.title}
                  </p>
                  <p
                    className={clsx(
                      "mt-1 text-sm leading-6",
                      authNotice.tone === "error" ? "text-rose-700" : "text-amber-700",
                    )}
                  >
                    {authNotice.message}
                  </p>
                </div>
              )}

              {errors.root && (
                <div className="rounded-md bg-red-50 p-4 border border-red-200">
                  <div className="flex"><div className="ml-3"><h3 className="text-sm font-medium text-red-800">{errors.root.message}</h3></div></div>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">Email</label>
                <div className="relative mt-2 rounded-md shadow-sm">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <UserIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    id="email" type="email" autoComplete="username" disabled={mutation.isPending}
                    className={clsx("block w-full rounded-md border-0 py-2.5 pl-10 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 transition-all", errors.email ? "ring-red-400 focus:ring-red-500" : "ring-gray-300")}
                    placeholder="Seu email" {...register("email")}
                  />
                </div>
                {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">Senha</label>
                <div className="relative mt-2 rounded-md shadow-sm">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <LockClosedIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    id="password" type={showPassword ? "text" : "password"} autoComplete="current-password" disabled={mutation.isPending}
                    className={clsx("block w-full rounded-md border-0 py-2.5 pl-10 pr-10 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 transition-all", errors.password ? "ring-red-400 focus:ring-red-500" : "ring-gray-300")}
                    placeholder="Sua senha" {...register("password")}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-gray-400 hover:text-gray-600 focus:outline-none">
                      {showPassword ? <EyeSlashIcon className="h-5 w-5" aria-hidden="true" /> : <EyeIcon className="h-5 w-5" aria-hidden="true" />}
                    </button>
                  </div>
                </div>
                {errors.password && <p className="mt-2 text-sm text-red-600">{errors.password.message}</p>}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 transition-colors"
                    {...register("rememberMe")}
                  />
                  <label htmlFor="remember-me" className="ml-3 block text-sm leading-6 text-gray-900">
                    Lembrar-me
                  </label>
                </div>
                <div className="text-sm leading-6">
                  <Link to="/forgot-password" className="font-semibold text-indigo-600 hover:text-indigo-500 transition-colors">
                    Esqueceu a senha?
                  </Link>
                </div>
              </div>

              <div>
                <button type="submit" disabled={mutation.isPending} className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors disabled:opacity-70">
                  {mutation.isPending ? "Entrando..." : "Entrar"}
                </button>
              </div>
            </form>
          </div>
        </div>
        
        {/* Version & Support Indicator */}
        <div className="absolute bottom-4 left-0 right-0 text-center">
          <p className="text-xs font-medium text-gray-400">Versão 0.1.0</p>
          {supportEmail && (
            <p className="mt-1 text-xs text-gray-400">
              Suporte:{' '}
              <a
                href={`mailto:${supportEmail}`}
                className="text-indigo-500 transition hover:text-indigo-600"
              >
                {supportEmail}
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
